import Hls from "hls.js";
import { useCallback, useEffect, useRef, type RefObject } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  getPlayback,
  listenFilter,
  startPlayback,
  type MediaPiece,
  type PlaybackPresentation,
  type PlaybackQueueItem,
  type PlaybackRequestSource,
} from "../api/queries";
import { ApiError } from "../api/client";
import { usePlayer, type PlaybackSource, type SeekTarget } from "../state/player";
import { segmentQuery, useListQuery, usePrefs } from "../state/prefs";
import { PLAYBACK_CHANGE_EVENT } from "../realtime/protocol";
import { useZone, type ActiveZone } from "../time/useZone";
import { nativeRuntime } from "../platform/native";

const CLOCK_OWNER = "net.jtarchive.playback.clock";
const RESUME_BACKOFF_MS = [250, 500, 1000, 2000, 4000];
const REFRESH_COOLDOWN_MS = 3_000;
const NATIVE_CLOCK_TIMEOUT_MS = 5_000;

export interface PlaybackHandles {
  audioRef: RefObject<HTMLAudioElement | null>;
  nudge: (seconds: number) => void;
}

export interface PlaybackClockInterval {
  audioStartS: number;
  audioEndS: number;
  mediaStartUs: number;
  mediaEndUs: number;
}

export type PlaybackSpanUs = readonly [startUs: number, endUs: number];

export interface PlaybackObservation {
  attachment: number;
  audio: HTMLAudioElement;
  presentation: PlaybackPresentation;
  clock: readonly PlaybackClockInterval[];
}

export interface AudibleItem {
  item: PlaybackQueueItem;
  mediaUs: number;
  itemPositionUs: number;
  itemDurationUs: number;
}

export interface PlaybackClockMarker {
  v: 1;
  presentationId: string;
  sequence: number;
  ticks90k: number;
}

type ObservationListener = (
  next: PlaybackObservation | null,
  previous: PlaybackObservation | null,
) => void;

let observation: PlaybackObservation | null = null;
const observationListeners = new Set<ObservationListener>();

export function currentPlaybackObservation(): PlaybackObservation | null {
  return observation;
}

export function subscribePlaybackObservation(listener: ObservationListener): () => void {
  observationListeners.add(listener);
  return () => observationListeners.delete(listener);
}

function publishObservation(next: PlaybackObservation | null): void {
  if (next === observation) return;
  const previous = observation;
  observation = next;
  for (const listener of observationListeners) listener(next, previous);
}

function metadataBytes(value: unknown): Uint8Array | null {
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (typeof value === "object" && value !== null && "data" in value) {
    return metadataBytes(value.data);
  }
  return null;
}

function synchsafe(bytes: Uint8Array, at: number): number | null {
  if (at + 4 > bytes.length) return null;
  const a = bytes[at];
  const b = bytes[at + 1];
  const c = bytes[at + 2];
  const d = bytes[at + 3];
  if (a === undefined || b === undefined || c === undefined || d === undefined) return null;
  if ((a | b | c | d) >= 0x80) return null;
  return (a << 21) | (b << 14) | (c << 7) | d;
}

function frameSize(bytes: Uint8Array, at: number, version: number): number | null {
  if (version === 4) return synchsafe(bytes, at);
  if (at + 4 > bytes.length) return null;
  return (
    (((bytes[at] ?? 0) << 24) |
      ((bytes[at + 1] ?? 0) << 16) |
      ((bytes[at + 2] ?? 0) << 8) |
      (bytes[at + 3] ?? 0)) >>>
    0
  );
}

export function parsePlaybackClock(value: unknown): PlaybackClockMarker | null {
  const bytes = metadataBytes(value);
  if (!bytes || bytes.length < 10 || bytes[0] !== 0x49 || bytes[1] !== 0x44 || bytes[2] !== 0x33) {
    return null;
  }
  const version = bytes[3];
  const tagSize = synchsafe(bytes, 6);
  if ((version !== 3 && version !== 4) || tagSize === null) return null;
  const end = Math.min(bytes.length, 10 + tagSize);
  const decoder = new TextDecoder();
  for (let at = 10; at + 10 <= end;) {
    const id = decoder.decode(bytes.subarray(at, at + 4));
    if (id === "\0\0\0\0") break;
    const size = frameSize(bytes, at + 4, version);
    if (size === null || size <= 0 || at + 10 + size > end) return null;
    if (id === "PRIV") {
      const payload = bytes.subarray(at + 10, at + 10 + size);
      const zero = payload.indexOf(0);
      if (zero >= 0 && decoder.decode(payload.subarray(0, zero)) === CLOCK_OWNER) {
        try {
          const raw = JSON.parse(decoder.decode(payload.subarray(zero + 1))) as Record<
            string,
            unknown
          >;
          if (
            raw.v !== 1 ||
            typeof raw.presentation_id !== "string" ||
            !Number.isInteger(raw.sequence) ||
            (raw.sequence as number) < 0 ||
            typeof raw.ticks90k !== "string" ||
            !/^\d+$/.test(raw.ticks90k)
          ) {
            return null;
          }
          const ticks = BigInt(raw.ticks90k);
          if (ticks > BigInt(Number.MAX_SAFE_INTEGER)) return null;
          return {
            v: 1,
            presentationId: raw.presentation_id,
            sequence: raw.sequence as number,
            ticks90k: Number(ticks),
          };
        } catch {
          return null;
        }
      }
    }
    at += 10 + size;
  }
  return null;
}

const CLOCK_TICK_US = 1_000_000 / 90_000;

function clockIntervalAt(
  clock: readonly PlaybackClockInterval[],
  audioTimeS: number,
): PlaybackClockInterval | null {
  let low = 0;
  let high = clock.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (clock[middle]!.audioStartS <= audioTimeS) low = middle + 1;
    else high = middle;
  }
  for (let index = low - 1; index >= 0; index -= 1) {
    const interval = clock[index]!;
    if (audioTimeS < interval.audioEndS) return interval;
    const previous = clock[index - 1];
    if (!previous || previous.audioEndS <= interval.audioStartS) break;
  }
  return null;
}

function mediaUsAtAudio(
  clock: readonly PlaybackClockInterval[],
  audioTimeS: number,
): number | null {
  if (!Number.isFinite(audioTimeS)) return null;
  const interval = clockIntervalAt(clock, audioTimeS);
  if (!interval) return null;
  return Math.round(interval.mediaStartUs + (audioTimeS - interval.audioStartS) * 1_000_000);
}

function audioTimeAtMedia(clock: readonly PlaybackClockInterval[], mediaUs: number): number | null {
  if (!Number.isFinite(mediaUs)) return null;
  let found: PlaybackClockInterval | null = null;
  for (const interval of clock) {
    if (interval.mediaStartUs <= mediaUs && mediaUs < interval.mediaEndUs) {
      if (
        !found ||
        interval.mediaStartUs > found.mediaStartUs ||
        (interval.mediaStartUs === found.mediaStartUs && interval.audioStartS > found.audioStartS)
      ) {
        found = interval;
      }
    }
  }
  return found ? found.audioStartS + (mediaUs - found.mediaStartUs) / 1_000_000 : null;
}

export function playbackMediaUs(observation: PlaybackObservation): number | null {
  return mediaUsAtAudio(observation.clock, observation.audio.currentTime);
}

export function playbackMediaSpans(
  observation: PlaybackObservation,
  audioSpansUs: readonly PlaybackSpanUs[],
): Array<[startUs: number, endUs: number]> {
  const mapped: Array<[number, number]> = [];
  for (const [audioStartUs, audioEndUs] of audioSpansUs) {
    if (
      !Number.isFinite(audioStartUs) ||
      !Number.isFinite(audioEndUs) ||
      audioEndUs <= audioStartUs
    ) {
      continue;
    }
    const cuts = [audioStartUs, audioEndUs];
    const audioStartS = audioStartUs / 1_000_000;
    const current = clockIntervalAt(observation.clock, audioStartS);
    if (current) {
      const end = current.audioEndS * 1_000_000;
      if (audioStartUs < end && end < audioEndUs) cuts.push(end);
    }
    let low = 0;
    let high = observation.clock.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (observation.clock[middle]!.audioStartS < audioStartS) low = middle + 1;
      else high = middle;
    }
    for (let index = low; index < observation.clock.length; index += 1) {
      const interval = observation.clock[index]!;
      const start = interval.audioStartS * 1_000_000;
      if (start >= audioEndUs) break;
      const end = interval.audioEndS * 1_000_000;
      if (audioStartUs < start) cuts.push(start);
      if (audioStartUs < end && end < audioEndUs) cuts.push(end);
    }
    cuts.sort((a, b) => a - b);
    for (let index = 1; index < cuts.length; index += 1) {
      const start = cuts[index - 1]!;
      const end = cuts[index]!;
      if (end <= start) continue;
      const interval = clockIntervalAt(observation.clock, (start + end) / 2_000_000);
      if (!interval) continue;
      const mediaStart = Math.round(
        interval.mediaStartUs + (start / 1_000_000 - interval.audioStartS) * 1_000_000,
      );
      const mediaEnd = Math.round(
        interval.mediaStartUs + (end / 1_000_000 - interval.audioStartS) * 1_000_000,
      );
      if (mediaEnd > mediaStart) mapped.push([mediaStart, mediaEnd]);
    }
  }
  return mapped;
}

interface FragmentClockInterval extends PlaybackClockInterval {
  sequence: number;
}

type SessionTiming =
  | { kind: "preparing" }
  | { kind: "ready"; clock: readonly FragmentClockInterval[] }
  | { kind: "failed" };

interface SessionRefs {
  audio: RefObject<HTMLAudioElement | null>;
  session: number;
  attachment: number;
  request: number;
  active: Attach | null;
  presentation: PlaybackPresentation | null;
  hls: Hls | null;
  retry: ReturnType<typeof setTimeout> | null;
  refreshTimer: ReturnType<typeof setTimeout> | null;
  nativeTimer: ReturnType<typeof setTimeout> | null;
  nativeCleanup: (() => void) | null;
  requestAbort: AbortController | null;
  refreshAbort: AbortController | null;
  attempt: number;
  pendingMediaUs: number | null;
  pendingAudioTimeS: number | null;
  pendingSeekToken: number;
  openingSeek: { attachment: number; token: number } | null;
  directSeek: { attachment: number; token: number } | null;
  clock: FragmentClockInterval[];
  fragmentClocks: WeakMap<object, PlaybackClockMarker>;
  timing: SessionTiming;
  refreshing: boolean;
  refreshPending: boolean;
  refreshNotBefore: number | null;
  t: TFunction;
  zone: ActiveZone;
}

interface Attach {
  refs: SessionRefs;
  audio: HTMLAudioElement;
  source: PlaybackSource;
  filter: Record<string, string | number | boolean>;
  session: number;
  lastRequest: PlaybackRequestSource | null;
  cancelled: boolean;
}

function useSessionRefs(): SessionRefs {
  const { t } = useTranslation();
  const zone = useZone();
  const audio = useRef<HTMLAudioElement | null>(null);
  const box = useRef<SessionRefs | null>(null);
  const refs: SessionRefs = box.current ?? {
    audio,
    session: 0,
    attachment: 0,
    request: 0,
    active: null,
    presentation: null,
    hls: null,
    retry: null,
    refreshTimer: null,
    nativeTimer: null,
    nativeCleanup: null,
    requestAbort: null,
    refreshAbort: null,
    attempt: 0,
    pendingMediaUs: null,
    pendingAudioTimeS: null,
    pendingSeekToken: 0,
    openingSeek: null,
    directSeek: null,
    clock: [],
    fragmentClocks: new WeakMap(),
    timing: { kind: "preparing" },
    refreshing: false,
    refreshPending: false,
    refreshNotBefore: null,
    t,
    zone,
  };
  box.current = refs;
  refs.t = t;
  refs.zone = zone;
  return refs;
}

function active(a: Attach, attachment?: number): boolean {
  return (
    !a.cancelled &&
    a.refs.active === a &&
    a.refs.session === a.session &&
    a.source === usePlayer.getState().source &&
    (attachment === undefined || a.refs.attachment === attachment)
  );
}

export function currentFilter(zone: string) {
  return listenFilter({
    ...segmentQuery(usePrefs.getState().listFilters, zone),
    q: useListQuery.getState().query || undefined,
    joined: false,
  });
}

export function playbackRequest(source: PlaybackSource, at?: string): PlaybackRequestSource | null {
  if (source.kind === "segment") {
    return {
      kind: "segment",
      segment_id: source.segment.id,
      at:
        at ??
        (source.atMs === undefined ? source.segment.start : new Date(source.atMs).toISOString()),
    };
  }
  if (source.kind === "window") {
    return { kind: "window", at: at ?? new Date(source.startMs).toISOString() };
  }
  if (source.kind === "dvr") {
    return { kind: "live", at: at ?? new Date(source.sourceAtMs).toISOString() };
  }
  if (source.kind === "presentation") return null;
  return at ? { kind: "live", at } : { kind: "live" };
}

function resumedMediaUs(
  presentation: PlaybackPresentation,
  source: Extract<PlaybackSource, { kind: "presentation" }>,
): number | null {
  if (source.sourceAt !== null) {
    const atMs = Date.parse(source.sourceAt);
    if (!Number.isFinite(atMs)) return null;
    const exact = mediaUsForSource(presentation, atMs, source.segmentId ?? undefined, null);
    if (exact !== null) return exact;
    const item = presentation.items.find((candidate) => {
      const start = Date.parse(candidate.span_start);
      const end = candidate.span_end === null ? Infinity : Date.parse(candidate.span_end);
      return candidate.segment_id === source.segmentId && start <= atMs && atMs < end;
    });
    const excised = item?.excisions.some((range) => {
      const start = Date.parse(range.start);
      const end = Date.parse(range.end);
      return start <= atMs && atMs < end;
    });
    if (!item || !excised) return null;
    const itemEnd = item.span_end === null ? Infinity : Date.parse(item.span_end);
    const next = presentation.map.pieces.find(
      (piece) =>
        piece.segment_id === source.segmentId &&
        piece.source_start !== null &&
        Date.parse(piece.source_start) >= atMs &&
        Date.parse(piece.source_start) < itemEnd,
    );
    return next?.media_start_us ?? null;
  }
  if (source.segmentId === null) return null;
  const matches = presentation.map.items.filter((item) => {
    if (item.segment_id !== source.segmentId) return false;
    const offset = item.item_offset_us ?? 0;
    const end = item.media_end_us ?? presentation.start_media_us + presentation.duration_us;
    return (
      offset <= source.itemPositionUs && source.itemPositionUs <= offset + end - item.media_start_us
    );
  });
  if (matches.length !== 1) return null;
  const item = matches[0]!;
  return item.media_start_us + source.itemPositionUs - (item.item_offset_us ?? 0);
}

export function pieceAt(presentation: PlaybackPresentation, mediaUs: number): MediaPiece | null {
  let found: MediaPiece | null = null;
  for (const piece of presentation.map.pieces) {
    if (piece.media_start_us > mediaUs) break;
    found = piece;
  }
  if (!found) return null;
  const final = presentation.map.pieces.at(-1) === found && found.media_end_us === mediaUs;
  return mediaUs < found.media_end_us || final ? found : null;
}

export function sourceMsAt(presentation: PlaybackPresentation, mediaUs: number): number | null {
  const quantizedPiece = presentation.map.pieces.find(
    (piece) => mediaUs < piece.media_start_us && piece.media_start_us - mediaUs <= CLOCK_TICK_US,
  );
  const piece = quantizedPiece ?? pieceAt(presentation, mediaUs);
  if (!piece?.source_start || !piece.source_end) return null;
  const sourceStart = Date.parse(piece.source_start);
  const sourceEnd = Date.parse(piece.source_end);
  const mediaDuration = piece.media_end_us - piece.media_start_us;
  if (!Number.isFinite(sourceStart) || !Number.isFinite(sourceEnd) || mediaDuration <= 0)
    return null;
  const mappedMediaUs = quantizedPiece ? piece.media_start_us : mediaUs;
  return (
    sourceStart +
    ((mappedMediaUs - piece.media_start_us) * (sourceEnd - sourceStart)) / mediaDuration
  );
}

function mediaUsAt(r: SessionRefs, audio: HTMLAudioElement): number | null {
  return r.presentation ? mediaUsAtAudio(r.clock, audio.currentTime) : null;
}

function mediaUsForSource(
  presentation: PlaybackPresentation,
  atMs: number,
  segmentId: number | undefined,
  currentMediaUs: number | null,
): number | null {
  const currentId =
    currentMediaUs === null ? null : pieceAt(presentation, currentMediaUs)?.segment_id;
  const preferredId = segmentId ?? currentId;
  const candidates = presentation.map.pieces.filter((piece) => {
    if (!piece.source_start || !piece.source_end) return false;
    const start = Date.parse(piece.source_start);
    const end = Date.parse(piece.source_end);
    return (
      start <= atMs && (atMs < end || (atMs === end && piece === presentation.map.pieces.at(-1)))
    );
  });
  const piece =
    preferredId === null
      ? candidates[0]
      : candidates.find((candidate) => candidate.segment_id === preferredId);
  if (!piece?.source_start || !piece.source_end) return null;
  const sourceStart = Date.parse(piece.source_start);
  const sourceEnd = Date.parse(piece.source_end);
  if (sourceEnd <= sourceStart) return null;
  return Math.round(
    piece.media_start_us +
      ((atMs - sourceStart) * (piece.media_end_us - piece.media_start_us)) /
        (sourceEnd - sourceStart),
  );
}

function clearTimer(timer: ReturnType<typeof setTimeout> | null): void {
  if (timer !== null) clearTimeout(timer);
}

function clearRetry(r: SessionRefs): void {
  clearTimer(r.retry);
  r.retry = null;
}

function clearRefresh(r: SessionRefs): void {
  clearTimer(r.refreshTimer);
  r.refreshTimer = null;
  r.refreshPending = false;
  r.refreshNotBefore = null;
}

function cancelRequest(r: SessionRefs): void {
  r.requestAbort?.abort();
  r.requestAbort = null;
}

function clearNative(r: SessionRefs): void {
  clearTimer(r.nativeTimer);
  r.nativeTimer = null;
  r.nativeCleanup?.();
  r.nativeCleanup = null;
}

function destroyTransport(r: SessionRefs): void {
  publishObservation(null);
  clearNative(r);
  r.hls?.destroy();
  r.hls = null;
  r.clock = [];
  r.fragmentClocks = new WeakMap();
  r.pendingAudioTimeS = null;
  r.timing = { kind: "preparing" };
  r.openingSeek = null;
  r.directSeek = null;
}

function applyVolume(r: SessionRefs): void {
  const audio = r.audio.current;
  if (!audio) return;
  if (r.timing.kind !== "ready") {
    audio.volume = 0;
    audio.muted = true;
    return;
  }
  const state = usePlayer.getState();
  audio.volume = state.volume;
  audio.muted = state.muted;
}

function publishReadyObservation(a: Attach, attachment: number): void {
  const r = a.refs;
  const presentation = r.presentation;
  if (!active(a, attachment) || r.timing.kind !== "ready" || !presentation) return;
  publishObservation({
    attachment,
    audio: a.audio,
    presentation,
    clock: r.clock,
  });
}

function prepareTiming(a: Attach): void {
  const r = a.refs;
  r.timing = { kind: "preparing" };
  publishObservation(null);
  applyVolume(r);
  const state = usePlayer.getState();
  usePlayer.setState({ playing: false, loading: state.wantsPlayback });
}

function failTiming(a: Attach, message = a.refs.t("player.timingUnavailable")): void {
  const r = a.refs;
  r.pendingMediaUs ??= mediaUsAt(r, a.audio);
  r.openingSeek = null;
  r.directSeek = null;
  r.timing = { kind: "failed" };
  publishObservation(null);
  applyVolume(r);
  usePlayer.setState({ error: message, playing: false, loading: false });
}

function readyTiming(a: Attach, attachment: number): boolean {
  const r = a.refs;
  const presentation = r.presentation;
  const mediaUs = mediaUsAt(r, a.audio);
  if (
    !active(a, attachment) ||
    r.clock.length === 0 ||
    !presentation ||
    mediaUs === null ||
    sourceMsAt(presentation, mediaUs) === null
  ) {
    return false;
  }
  r.timing = { kind: "ready", clock: r.clock };
  r.attempt = 0;
  applyVolume(r);
  publishReadyObservation(a, attachment);
  const state = usePlayer.getState();
  usePlayer.setState({
    error: null,
    loading: false,
    playing: state.wantsPlayback && !a.audio.paused,
  });
  return true;
}

function isAutoplayRefusal(error: unknown): boolean {
  return error instanceof DOMException && error.name === "NotAllowedError";
}

function terminalPlaybackError(r: SessionRefs, error: unknown): string | null {
  if (
    !(error instanceof ApiError) ||
    error.status < 400 ||
    error.status >= 500 ||
    error.status === 408 ||
    error.status === 429
  ) {
    return null;
  }
  if (error.errorCode === "playback_recording_gap") return r.t("player.recordingGap");
  if (error.errorCode === "playback_media_changed") return r.t("player.mediaChanged");
  return r.t("player.streamFailed");
}

function retryAfter(error: unknown): number | null {
  if (!(error instanceof ApiError) || (error.status !== 429 && error.status !== 503)) {
    return null;
  }
  return error.retryAfterMs;
}

function nextRetryDelay(r: SessionRefs, retryAfterMs: number | null): number | null {
  const fallback = RESUME_BACKOFF_MS[r.attempt];
  if (fallback === undefined) {
    const a = r.active;
    if (a && active(a)) failTiming(a, r.t("player.streamFailed"));
    else usePlayer.setState({ error: r.t("player.streamFailed"), playing: false, loading: false });
    return null;
  }
  r.attempt += 1;
  return retryAfterMs ?? fallback;
}

function prime(a: Attach, attachment = a.refs.attachment): void {
  if (!active(a, attachment) || !usePlayer.getState().wantsPlayback) return;
  applyVolume(a.refs);
  void a.audio.play().catch((error: unknown) => {
    if (!active(a, attachment)) return;
    if (isAutoplayRefusal(error)) {
      usePlayer.getState().pause();
      usePlayer.getState().setPlaying(false);
      usePlayer.getState().setLoading(false);
      return;
    }
    scheduleResume(a);
  });
}
function finishOpeningSeek(a: Attach, attachment: number, token: number): void {
  const r = a.refs;
  if (
    !active(a, attachment) ||
    r.openingSeek?.attachment !== attachment ||
    r.openingSeek.token !== token ||
    usePlayer.getState().seekToken !== token
  ) {
    return;
  }
  if (r.pendingMediaUs !== null) {
    const actual = mediaUsAt(r, a.audio);
    if (actual === null || Math.abs(actual - r.pendingMediaUs) > CLOCK_TICK_US + 1) return;
  }
  if (!readyTiming(a, attachment)) {
    if (r.presentation?.ended) failTiming(a);
    else refreshPresentation(a, true);
    return;
  }
  r.pendingMediaUs = null;
  r.pendingAudioTimeS = null;
  r.openingSeek = null;
  clearTimeout(r.nativeTimer ?? undefined);
  r.nativeTimer = null;
  updatePosition(a);
  if (usePlayer.getState().wantsPlayback) prime(a, attachment);
  else a.audio.pause();
}

function beginOpeningSeek(a: Attach, attachment: number): void {
  const r = a.refs;
  const presentation = r.presentation;
  if (!active(a, attachment) || !presentation || r.timing.kind === "ready") return;
  const token = r.pendingSeekToken;
  if (usePlayer.getState().seekToken !== token) return;
  let mediaUs = r.pendingMediaUs;
  let audioTime: number | null;
  if (r.pendingAudioTimeS !== null) {
    mediaUs = mediaUsAtAudio(r.clock, r.pendingAudioTimeS);
    audioTime = mediaUs === null ? null : r.pendingAudioTimeS;
  } else {
    mediaUs ??= presentation.start_media_us;
    audioTime = audioTimeAtMedia(r.clock, mediaUs);
  }
  if (mediaUs === null || audioTime === null) return;
  const windowEnd = presentation.start_media_us + presentation.duration_us;
  if (mediaUs < presentation.start_media_us || mediaUs > windowEnd) {
    refreshPresentation(a, true);
    return;
  }
  if (sourceMsAt(presentation, mediaUs) === null) {
    r.pendingMediaUs = mediaUs;
    if (presentation.ended) failTiming(a);
    else refreshPresentation(a, true);
    return;
  }
  r.pendingMediaUs = mediaUs;
  r.openingSeek = { attachment, token };
  a.audio.pause();
  try {
    a.audio.currentTime = Math.max(0, audioTime);
  } catch {
    failTiming(a);
    return;
  }
  queueMicrotask(() => {
    if (!a.audio.seeking) finishOpeningSeek(a, attachment, token);
  });
}

function addClockInterval(
  a: Attach,
  attachment: number,
  interval: FragmentClockInterval,
): "added" | "same" | "conflict" {
  const r = a.refs;
  const existing = r.clock.find((candidate) => candidate.sequence === interval.sequence);
  if (existing) {
    if (existing.mediaStartUs !== interval.mediaStartUs) return "conflict";
    if (
      existing.audioStartS === interval.audioStartS &&
      existing.audioEndS === interval.audioEndS &&
      existing.mediaEndUs === interval.mediaEndUs
    ) {
      return "same";
    }
    r.clock = r.clock.filter((candidate) => candidate !== existing);
  }
  r.clock = [...r.clock, interval].sort(
    (left, right) =>
      left.audioStartS - right.audioStartS ||
      left.mediaStartUs - right.mediaStartUs ||
      left.sequence - right.sequence,
  );
  if (r.timing.kind === "ready") {
    const mediaUs = mediaUsAt(r, a.audio);
    const presentation = r.presentation;
    if (mediaUs === null || !presentation || sourceMsAt(presentation, mediaUs) === null) {
      r.pendingAudioTimeS = a.audio.currentTime;
      r.pendingMediaUs = mediaUs;
      r.pendingSeekToken = usePlayer.getState().seekToken;
      r.openingSeek = null;
      r.directSeek = null;
      a.audio.pause();
      prepareTiming(a);
    } else {
      r.timing = { kind: "ready", clock: r.clock };
      publishReadyObservation(a, attachment);
    }
  }
  return "added";
}

function markerMediaUs(marker: PlaybackClockMarker): number | null {
  const mediaUs = Math.round((marker.ticks90k / 90_000) * 1_000_000);
  return Number.isSafeInteger(mediaUs) ? mediaUs : null;
}

function acceptClockInterval(
  a: Attach,
  presentationId: string,
  attachment: number,
  marker: PlaybackClockMarker | null,
  audioStartS: number,
  audioEndS: number,
): void {
  const r = a.refs;
  if (
    !active(a, attachment) ||
    r.presentation?.id !== presentationId ||
    marker === null ||
    marker.presentationId !== presentationId
  ) {
    return;
  }
  const mediaStartUs = markerMediaUs(marker);
  const durationUs = Math.round((audioEndS - audioStartS) * 1_000_000);
  const mediaEndUs = mediaStartUs === null ? Number.NaN : mediaStartUs + durationUs;
  if (
    !Number.isFinite(audioStartS) ||
    !Number.isFinite(audioEndS) ||
    audioEndS <= audioStartS ||
    mediaStartUs === null ||
    mediaStartUs < 0 ||
    !Number.isSafeInteger(mediaEndUs) ||
    durationUs <= 0
  ) {
    failTiming(a);
    return;
  }
  const result = addClockInterval(a, attachment, {
    sequence: marker.sequence,
    audioStartS,
    audioEndS,
    mediaStartUs,
    mediaEndUs,
  });
  if (result === "conflict") {
    failTiming(a);
    return;
  }
  const presentation = r.presentation;
  const windowEnd = presentation.start_media_us + presentation.duration_us;
  if (
    !presentation.ended &&
    (mediaEndUs < presentation.start_media_us - CLOCK_TICK_US ||
      mediaStartUs > windowEnd + CLOCK_TICK_US)
  ) {
    refreshPresentation(a);
  }
  if (r.timing.kind !== "ready") beginOpeningSeek(a, attachment);
}

function acceptNativeClock(
  a: Attach,
  presentationId: string,
  attachment: number,
  cue: TextTrackCue,
  marker: PlaybackClockMarker | null,
): void {
  const presentation = a.refs.presentation;
  if (!presentation || presentation.id !== presentationId || marker === null) return;
  let audioEndS = cue.endTime;
  if (!Number.isFinite(audioEndS) || audioEndS <= cue.startTime) {
    const mediaStartUs = markerMediaUs(marker);
    const piece =
      mediaStartUs === null
        ? null
        : presentation.map.pieces.find(
            (candidate) =>
              candidate.media_start_us - CLOCK_TICK_US <= mediaStartUs &&
              mediaStartUs < candidate.media_end_us + CLOCK_TICK_US,
          );
    if (!piece || mediaStartUs === null) {
      if (!presentation.ended) refreshPresentation(a, true);
      return;
    }
    audioEndS = cue.startTime + (piece.media_end_us - mediaStartUs) / 1_000_000;
  }
  a.refs.clock = a.refs.clock.filter(
    (interval) => interval.sequence === marker.sequence || interval.audioStartS !== cue.startTime,
  );
  acceptClockInterval(a, presentationId, attachment, marker, cue.startTime, audioEndS);
}

function installNativeClock(a: Attach, presentationId: string, attachment: number): () => void {
  const cleanups: Array<() => void> = [];
  const addTrack = (track: TextTrack): void => {
    track.mode = "hidden";
    const cueChange = () => {
      const cues = track.activeCues;
      if (!cues) return;
      for (let index = 0; index < cues.length; index += 1) {
        const cue = cues[index] as TextTrackCue & {
          type?: string;
          value?: unknown;
          data?: unknown;
        };
        if (cue.type !== undefined && cue.type !== "org.id3") continue;
        acceptNativeClock(
          a,
          presentationId,
          attachment,
          cue,
          parsePlaybackClock(cue.value ?? cue.data),
        );
      }
    };
    track.addEventListener("cuechange", cueChange);
    cleanups.push(() => track.removeEventListener("cuechange", cueChange));
  };
  const tracks = a.audio.textTracks;
  for (let index = 0; index < tracks.length; index += 1) {
    const track = tracks[index];
    if (track) addTrack(track);
  }
  const added = (event: Event) => {
    const track = (event as Event & { track?: TextTrack }).track;
    if (track) addTrack(track);
  };
  tracks.addEventListener("addtrack", added);
  cleanups.push(() => tracks.removeEventListener("addtrack", added));
  return () => {
    for (const cleanup of cleanups) cleanup();
  };
}

function attachTransport(
  a: Attach,
  presentation: PlaybackPresentation,
  mediaUs = presentation.start_media_us,
  forceHls = false,
): void {
  const r = a.refs;
  if (!active(a)) return;
  destroyTransport(r);
  const attachment = ++r.attachment;
  r.presentation = presentation;
  r.pendingMediaUs = mediaUs;
  r.pendingSeekToken = usePlayer.getState().seekToken;
  prepareTiming(a);
  a.audio.removeAttribute("src");
  a.audio.load();

  const clockless = presentation.playlist_url.startsWith("/api/hls/segment/");
  const native =
    !forceHls &&
    Boolean(a.audio.canPlayType("application/vnd.apple.mpegurl")) &&
    typeof (a.audio as HTMLAudioElement & { getStartDate?: unknown }).getStartDate === "function";
  if (native) {
    if (clockless) {
      r.clock = [
        {
          sequence: -1,
          audioStartS: presentation.start_media_us / 1_000_000,
          audioEndS: (presentation.start_media_us + presentation.duration_us) / 1_000_000,
          mediaStartUs: presentation.start_media_us,
          mediaEndUs: presentation.start_media_us + presentation.duration_us,
        },
      ];
      const loaded = () => beginOpeningSeek(a, attachment);
      a.audio.addEventListener("loadedmetadata", loaded, { once: true });
      r.nativeCleanup = () => a.audio.removeEventListener("loadedmetadata", loaded);
    } else {
      const cleanupClock = installNativeClock(a, presentation.id, attachment);
      const loaded = () => {
        if (!active(a, attachment) || r.timing.kind === "ready") return;
        try {
          a.audio.currentTime = Math.max(
            0,
            ((r.pendingMediaUs ?? mediaUs) - presentation.start_media_us) / 1_000_000,
          );
        } catch {
          failTiming(a);
        }
      };
      a.audio.addEventListener("loadedmetadata", loaded, { once: true });
      r.nativeCleanup = () => {
        cleanupClock();
        a.audio.removeEventListener("loadedmetadata", loaded);
      };
    }
    a.audio.src = presentation.playlist_url;
    a.audio.load();
    if (!clockless) {
      r.nativeTimer = setTimeout(() => {
        if (!active(a, attachment) || r.timing.kind === "ready") return;
        if (Hls.isSupported()) attachTransport(a, presentation, mediaUs, true);
        else failTiming(a, r.t("player.unsupportedPlayback"));
      }, NATIVE_CLOCK_TIMEOUT_MS);
    }
    prime(a, attachment);
    return;
  }

  if (!Hls.isSupported()) {
    failTiming(a, r.t("player.unsupportedPlayback"));
    return;
  }
  const hls = new Hls({
    enableWorker: true,
    enableWebVTT: false,
    enableID3MetadataCues: false,
    enableEmsgMetadataCues: false,
    enableDateRangeMetadataCues: false,
    startPosition: Math.max(0, (mediaUs - presentation.start_media_us) / 1_000_000),
  });
  r.hls = hls;
  hls.on(Hls.Events.MEDIA_ATTACHED, () => {
    if (active(a, attachment)) hls.loadSource(presentation.playlist_url);
  });
  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    if (clockless) {
      r.clock = [
        {
          sequence: -1,
          audioStartS: presentation.start_media_us / 1_000_000,
          audioEndS: (presentation.start_media_us + presentation.duration_us) / 1_000_000,
          mediaStartUs: presentation.start_media_us,
          mediaEndUs: presentation.start_media_us + presentation.duration_us,
        },
      ];
      beginOpeningSeek(a, attachment);
    }
    prime(a, attachment);
  });
  hls.on(Hls.Events.FRAG_PARSING_METADATA, (_event, data) => {
    if (!active(a, attachment) || typeof data.frag.sn !== "number") return;
    let marker: PlaybackClockMarker | null = null;
    for (const sample of data.samples) {
      const candidate = parsePlaybackClock(sample.data);
      if (
        !candidate ||
        candidate.presentationId !== presentation.id ||
        candidate.sequence !== data.frag.sn
      ) {
        continue;
      }
      if (
        marker &&
        (marker.presentationId !== candidate.presentationId ||
          marker.sequence !== candidate.sequence ||
          marker.ticks90k !== candidate.ticks90k)
      ) {
        failTiming(a);
        return;
      }
      marker = candidate;
    }
    if (marker) r.fragmentClocks.set(data.frag, marker);
  });
  hls.on(Hls.Events.FRAG_BUFFERED, (_event, data) => {
    if (!active(a, attachment)) return;
    const marker = r.fragmentClocks.get(data.frag);
    if (!marker) return;
    r.fragmentClocks.delete(data.frag);
    if (typeof data.frag.sn !== "number" || marker.sequence !== data.frag.sn) return;
    const audio = data.frag.elementaryStreams.audio;
    if (!audio) {
      failTiming(a);
      return;
    }
    acceptClockInterval(a, presentation.id, attachment, marker, audio.startPTS, audio.endPTS);
  });
  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (active(a, attachment) && data.fatal) scheduleResume(a);
  });
  hls.attachMedia(a.audio);
}

function aborted(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

async function openPresentation(
  a: Attach,
  source: PlaybackRequestSource | null,
  seekToken = usePlayer.getState().seekToken,
): Promise<void> {
  const r = a.refs;
  r.requestAbort?.abort();
  const abort = new AbortController();
  r.requestAbort = abort;
  const request = ++r.request;
  a.lastRequest = source;
  usePlayer.getState().setLoading(true);
  const existing = source === null && a.source.kind === "presentation" ? a.source : null;
  try {
    let presentation: PlaybackPresentation | undefined;
    let resumed: number | null | undefined;
    if (existing?.presentationId) {
      try {
        presentation = await getPlayback(existing.presentationId, abort.signal);
      } catch (error) {
        if (
          !(error instanceof ApiError) ||
          (error.status !== 404 && error.status !== 410) ||
          !active(a) ||
          r.requestAbort !== abort ||
          !usePlayer.getState().wantsPlayback
        ) {
          throw error;
        }
      }
    }
    if (presentation && existing) {
      resumed = resumedMediaUs(presentation, existing);
      if (resumed === null) presentation = undefined;
    }
    if (!presentation) {
      const at = existing?.sourceAt ?? undefined;
      const renewed = existing
        ? existing.segmentId !== null
          ? {
              kind: "segment" as const,
              segment_id: existing.segmentId,
              ...(at === undefined ? { item_position_us: existing.itemPositionUs } : { at }),
            }
          : at
            ? { kind: "window" as const, at }
            : null
        : source;
      if (renewed === null) {
        usePlayer.getState().setError(r.t("player.timingUnavailable"));
        usePlayer.getState().pause();
        return;
      }
      presentation = await startPlayback(a.filter, renewed, abort.signal);
    }
    if (
      !active(a) ||
      r.request !== request ||
      r.requestAbort !== abort ||
      usePlayer.getState().seekToken !== seekToken
    ) {
      return;
    }
    // A fresh presentation has already resolved the request to a playable frame boundary.
    const mediaUs = resumed ?? presentation.start_media_us;
    r.requestAbort = null;
    r.attempt = 0;
    r.pendingSeekToken = seekToken;
    attachTransport(a, presentation, mediaUs);
  } catch (error) {
    const message = terminalPlaybackError(r, error);
    if (message !== null && active(a) && r.requestAbort === abort) {
      usePlayer.setState({
        error: message,
        playing: false,
        loading: false,
        wantsPlayback: false,
      });
    } else if (!aborted(error) && active(a) && r.requestAbort === abort) {
      scheduleResume(a, retryAfter(error));
    }
  } finally {
    if (r.requestAbort === abort) r.requestAbort = null;
  }
}

function scheduleResume(a: Attach, retryAfterMs: number | null = null): void {
  const r = a.refs;
  if (!active(a) || r.retry !== null || !usePlayer.getState().wantsPlayback) return;
  const delay = nextRetryDelay(r, retryAfterMs);
  if (delay === null) return;
  usePlayer.setState({ playing: false, loading: true });
  const mediaUs = mediaUsAt(r, a.audio) ?? r.pendingMediaUs;
  const session = a.session;
  r.retry = setTimeout(() => {
    r.retry = null;
    if (!active(a) || a.session !== session || !usePlayer.getState().wantsPlayback) return;
    const presentation = r.presentation;
    if (presentation) attachTransport(a, presentation, mediaUs ?? presentation.start_media_us);
    else void openPresentation(a, a.lastRequest);
  }, delay);
}

function scheduleRefreshRetry(a: Attach, error: unknown): boolean {
  if (!(error instanceof ApiError) || (error.status !== 429 && error.status !== 503)) {
    return false;
  }
  const r = a.refs;
  r.refreshPending = false;
  if (!active(a) || !usePlayer.getState().wantsPlayback) return true;
  const delay = nextRetryDelay(r, error.retryAfterMs);
  if (delay === null) return true;
  const now = Date.now();
  const refreshNotBefore =
    r.refreshNotBefore === null ? now + delay : Math.max(r.refreshNotBefore, now + delay);
  r.refreshNotBefore = refreshNotBefore;
  clearTimer(r.refreshTimer);
  const wait = Math.max(0, refreshNotBefore - now);
  r.refreshTimer = setTimeout(() => {
    r.refreshTimer = null;
    if (active(a) && usePlayer.getState().wantsPlayback) refreshPresentation(a, true);
  }, wait);
  return true;
}

function refreshPresentation(a: Attach, force = false): void {
  const r = a.refs;
  const presentation = r.presentation;
  if (!presentation || presentation.ended) return;
  if (r.refreshing) {
    if (force) r.refreshPending = true;
    return;
  }
  const wait = r.refreshNotBefore === null ? 0 : Math.max(0, r.refreshNotBefore - Date.now());
  if (wait > 0) {
    if (force) {
      r.refreshPending = true;
      if (r.refreshTimer === null) {
        r.refreshTimer = setTimeout(() => {
          r.refreshTimer = null;
          if (active(a)) refreshPresentation(a, true);
        }, wait);
      }
    }
    return;
  }
  clearTimer(r.refreshTimer);
  r.refreshTimer = null;
  r.refreshPending = false;
  const abort = new AbortController();
  r.refreshAbort = abort;
  r.refreshing = true;
  r.refreshNotBefore = Date.now() + REFRESH_COOLDOWN_MS;
  const session = a.session;
  const demandMediaUs = r.pendingMediaUs ?? mediaUsAt(r, a.audio) ?? undefined;
  void getPlayback(presentation.id, abort.signal, demandMediaUs)
    .then((fresh) => {
      if (
        active(a) &&
        a.session === session &&
        r.refreshAbort === abort &&
        r.presentation?.id === fresh.id
      ) {
        r.presentation = fresh;
        r.attempt = 0;
        const pendingMediaUs = r.pendingMediaUs;
        const windowEnd = fresh.start_media_us + fresh.duration_us;
        if (
          pendingMediaUs !== null &&
          fresh.start_media_us <= pendingMediaUs &&
          pendingMediaUs <= windowEnd
        ) {
          if (r.pendingAudioTimeS !== null) {
            if (sourceMsAt(fresh, pendingMediaUs) !== null) beginOpeningSeek(a, r.attachment);
            return;
          }
          attachTransport(a, fresh, pendingMediaUs);
          return;
        }
        if (r.timing.kind === "ready") publishReadyObservation(a, r.attachment);
      }
    })
    .catch((error: unknown) => {
      if (aborted(error) || !active(a)) return;
      const message = terminalPlaybackError(r, error);
      if (message !== null) {
        clearRefresh(r);
        failTiming(a, message);
        usePlayer.setState({
          error: message,
          playing: false,
          loading: false,
          wantsPlayback: false,
        });
      } else {
        r.refreshPending = false;
        if (!scheduleRefreshRetry(a, error)) scheduleResume(a);
      }
    })
    .finally(() => {
      if (r.refreshAbort === abort) {
        r.refreshAbort = null;
        r.refreshing = false;
        if (r.refreshPending) refreshPresentation(a, true);
      }
    });
}

function updatePosition(a: Attach): void {
  const r = a.refs;
  if (!active(a) || r.timing.kind !== "ready" || a.audio.readyState === 0 || a.audio.seeking) {
    return;
  }
  if (usePlayer.getState().seekToken !== r.pendingSeekToken) return;
  const mediaUs = mediaUsAt(r, a.audio);
  const presentation = r.presentation;
  if (!presentation) return;
  const sourceMs = mediaUs === null ? null : sourceMsAt(presentation, mediaUs);
  if (mediaUs === null || sourceMs === null) {
    r.pendingAudioTimeS = a.audio.currentTime;
    r.pendingMediaUs = mediaUs;
    r.pendingSeekToken = usePlayer.getState().seekToken;
    r.openingSeek = null;
    r.directSeek = null;
    a.audio.pause();
    prepareTiming(a);
    if (mediaUs !== null) refreshPresentation(a, true);
    return;
  }
  usePlayer.getState().setPosition(sourceMs);
  const windowEnd = presentation.start_media_us + presentation.duration_us;
  if (!presentation.ended && windowEnd - mediaUs <= 30_000_000) refreshPresentation(a);
}

function mediaListeners(a: Attach): Record<string, () => void> {
  const r = a.refs;
  return {
    timeupdate: () => updatePosition(a),
    loadedmetadata: () => prime(a),
    seeked: () => {
      const opening = r.openingSeek;
      if (opening) finishOpeningSeek(a, opening.attachment, opening.token);
      const direct = r.directSeek;
      if (
        direct &&
        active(a, direct.attachment) &&
        usePlayer.getState().seekToken === direct.token
      ) {
        r.directSeek = null;
        updatePosition(a);
      }
    },
    play: () => {
      if (r.timing.kind === "ready") usePlayer.getState().setPlaying(true);
    },
    pause: () => usePlayer.getState().setPlaying(false),
    playing: () => {
      if (r.timing.kind !== "ready") return;
      clearRetry(r);
      r.attempt = 0;
      usePlayer.setState({ error: null, playing: true, loading: false });
    },
    waiting: () => {
      usePlayer.setState({ playing: false, loading: true });
    },
    ended: () => {
      if (r.presentation?.ended) usePlayer.getState().end();
      else scheduleResume(a);
    },
    error: () => scheduleResume(a),
    stalled: () => scheduleResume(a),
  };
}

function detachSource(r: SessionRefs, audio: HTMLAudioElement): void {
  r.session += 1;
  r.attachment += 1;
  r.request += 1;
  r.active = null;
  r.presentation = null;
  r.pendingMediaUs = null;
  cancelRequest(r);
  r.refreshAbort?.abort();
  r.refreshAbort = null;
  r.refreshing = false;
  clearRefresh(r);
  clearRetry(r);
  destroyTransport(r);
  audio.removeAttribute("src");
  audio.load();
}

function attachSession(
  r: SessionRefs,
  audio: HTMLAudioElement,
  source: PlaybackSource,
  filter: Record<string, string | number | boolean>,
): () => void {
  clearRefresh(r);
  const session = ++r.session;
  const request = playbackRequest(source);
  const a: Attach = {
    refs: r,
    audio,
    source,
    filter,
    session,
    lastRequest: request,
    cancelled: false,
  };
  r.active = a;
  r.attempt = 0;
  usePlayer.setState({
    loading: usePlayer.getState().wantsPlayback,
    playing: false,
    error: null,
  });
  const listeners = Object.entries(mediaListeners(a));
  for (const [type, listener] of listeners) audio.addEventListener(type, listener);
  if (usePlayer.getState().wantsPlayback) void openPresentation(a, request);

  return () => {
    a.cancelled = true;
    for (const [type, listener] of listeners) audio.removeEventListener(type, listener);
    if (r.active === a) detachSource(r, audio);
  };
}

function seekMedia(r: SessionRefs, mediaUs: number, token: number): void {
  const a = r.active;
  const presentation = r.presentation;
  if (!a || !presentation || !active(a)) return;
  r.pendingSeekToken = token;
  const windowEnd = presentation.start_media_us + presentation.duration_us;
  if (!presentation.ended && (mediaUs < presentation.start_media_us || mediaUs >= windowEnd)) {
    r.pendingMediaUs = Math.max(0, mediaUs);
    refreshPresentation(a, true);
    return;
  }
  let clamped = Math.min(
    presentation.start_media_us + presentation.duration_us,
    Math.max(presentation.start_media_us, mediaUs),
  );
  if (presentation.ended && clamped === windowEnd) {
    // The endpoint is outside the half-open clock; stop on its last playable tick.
    clamped = Math.max(presentation.start_media_us, windowEnd - Math.ceil(CLOCK_TICK_US));
    usePlayer.getState().end();
    a.audio.pause();
  }
  const audioTime = audioTimeAtMedia(r.clock, clamped);
  if (r.timing.kind !== "ready" || audioTime === null) {
    r.pendingMediaUs = clamped;
    r.pendingSeekToken = token;
    attachTransport(a, presentation, clamped);
    return;
  }
  r.directSeek = { attachment: r.attachment, token };
  try {
    a.audio.currentTime = Math.max(0, audioTime);
  } catch {
    failTiming(a);
    return;
  }
  queueMicrotask(() => {
    if (!a.audio.seeking && r.directSeek?.token === token) {
      r.directSeek = null;
      updatePosition(a);
    }
  });
  prime(a);
}

function seekTo(r: SessionRefs, target: SeekTarget, token: number): void {
  const a = r.active;
  if (!a || !active(a)) return;
  if (target.kind === "media") {
    seekMedia(r, target.mediaUs, token);
    return;
  }
  const currentMediaUs = mediaUsAt(r, a.audio);
  const presentation = r.presentation;
  const targetMediaUs = presentation
    ? mediaUsForSource(presentation, target.atMs, target.segmentId, currentMediaUs)
    : null;
  if (targetMediaUs !== null) {
    seekMedia(r, targetMediaUs, token);
    return;
  }
  const at = new Date(target.atMs).toISOString();
  const request: PlaybackRequestSource = target.segmentId
    ? { kind: "segment", segment_id: target.segmentId, at }
    : (playbackRequest(a.source, at) ?? { kind: "window", at });
  void openPresentation(a, request, token);
}

function nudgeBy(r: SessionRefs, seconds: number): void {
  const audio = r.audio.current;
  const presentation = r.presentation;
  if (!audio || !presentation) return;
  const mediaUs = mediaUsAt(r, audio) ?? r.pendingMediaUs ?? presentation.start_media_us;
  usePlayer.getState().requestSeek({ kind: "media", mediaUs: mediaUs + seconds * 1_000_000 });
}

export function currentAudibleItem(): AudibleItem | null {
  const current = observation;
  if (!current) return null;
  const mediaUs = playbackMediaUs(current);
  if (mediaUs === null) return null;
  const mappedIndex = current.presentation.map.items.findIndex(
    (item) =>
      item.media_start_us <= mediaUs &&
      (item.media_end_us === null || mediaUs <= item.media_end_us),
  );
  const mapped = current.presentation.map.items[mappedIndex];
  if (!mapped) return null;

  let occurrence = 0;
  for (let index = 0; index < mappedIndex; index += 1) {
    if (current.presentation.map.items[index]?.segment_id === mapped.segment_id) occurrence += 1;
  }
  let item: PlaybackQueueItem | undefined;
  for (const candidate of current.presentation.items) {
    if (candidate.segment_id !== mapped.segment_id) continue;
    if (occurrence === 0) {
      item = candidate;
      break;
    }
    occurrence -= 1;
  }
  if (!item) return null;
  const end =
    mapped.media_end_us ?? current.presentation.start_media_us + current.presentation.duration_us;
  return {
    item,
    mediaUs,
    itemPositionUs: Math.max(0, (mapped.item_offset_us ?? 0) + mediaUs - mapped.media_start_us),
    itemDurationUs: Math.max(0, (mapped.item_offset_us ?? 0) + end - mapped.media_start_us),
  };
}

export function useAudioSession(): PlaybackHandles {
  const r = useSessionRefs();
  const source = usePlayer((state) => state.source);
  const wantsPlayback = usePlayer((state) => state.wantsPlayback);
  const volume = usePlayer((state) => state.volume);
  const muted = usePlayer((state) => state.muted);
  const seekRequest = usePlayer((state) => state.seekRequest);
  const browser = nativeRuntime() === null;
  const filterNow = useCallback(() => currentFilter(r.zone.name), [r]);

  useEffect(() => {
    if (!browser) return;
    const audio = r.audio.current;
    if (!audio) return;
    if (!source) {
      detachSource(r, audio);
      return;
    }
    if (source.kind === "segment" && !source.segment.playable) {
      const store = usePlayer.getState();
      store.setError(r.t("player.audioExpired"));
      store.pause();
      return;
    }
    return attachSession(
      r,
      audio,
      source,
      source.kind === "presentation" && source.filter ? source.filter : filterNow(),
    );
  }, [browser, r, filterNow, source]);

  useEffect(() => {
    if (!browser) return;
    const audio = r.audio.current;
    const a = r.active;
    if (!audio || !a) return;
    if (wantsPlayback) {
      if (!r.presentation && !r.requestAbort) void openPresentation(a, a.lastRequest);
      else if (r.presentation) prime(a);
    } else {
      clearRetry(r);
      cancelRequest(r);
      usePlayer.getState().setLoading(false);
      audio.pause();
    }
  }, [browser, r, wantsPlayback]);

  useEffect(() => {
    if (!browser) return;
    const refresh = () => {
      const active = r.active;
      if (active && usePlayer.getState().wantsPlayback) refreshPresentation(active, true);
    };
    window.addEventListener(PLAYBACK_CHANGE_EVENT, refresh);
    return () => window.removeEventListener(PLAYBACK_CHANGE_EVENT, refresh);
  }, [browser, r]);

  useEffect(() => {
    if (browser) applyVolume(r);
  }, [browser, r, volume, muted]);

  useEffect(() => {
    if (!browser) return;
    if (!seekRequest || !usePlayer.getState().source) return;
    seekTo(r, seekRequest.target, seekRequest.token);
    if (usePlayer.getState().seekRequest?.token === seekRequest.token) {
      usePlayer.getState().clearSeek();
    }
  }, [browser, r, seekRequest]);

  const nudge = useCallback((seconds: number) => nudgeBy(r, seconds), [r]);

  useEffect(() => {
    if (!browser) return;
    const audio = r.audio.current;
    return () => {
      if (audio) detachSource(r, audio);
    };
  }, [browser, r]);

  return { audioRef: r.audio, nudge };
}
